// =============================================================================
// DeliverySelect.jsx — registered-address radio-cards with "Other..." fallback
// =============================================================================
// Renders: a list of the shelter's registered delivery addresses as radio-style
//          cards, plus a "Different address" card that reveals an
//          AddressAutocomplete for one-off addresses. When the current value
//          isn't a registered match, the autocomplete is shown with an
//          "address not verified" warning.
// Props:
//   value              - current delivery address as a single string
//   onChange(text)     - called when user picks or types
//   deliveryAddresses  - array of registered address row objects (with street,
//                        suburb, state, postcode, address_name)
//   locked             - if true, render read-only
// Notes: format used to match a string to a registered row is
//        [street, street2, suburb, state, postcode].filter(Boolean).join(", ")
//        — matched by resolveDeliveryForSave in helpers.js at save time.
// =============================================================================

import React, { useId } from "react";
import { AddressAutocomplete } from "./AddressAutocomplete.jsx";
import { LockedField } from "./FormUI.jsx";

// onPartsChange(parts | null) — OPTIONAL. When the user PICKS an address from the
// Google autocomplete, surface its structured components (db-96/verified-address
// shape: { line1, line2, suburb, state, postcode }) so the submit path can create a
// structured one-off delivery_addresses row instead of storing a flat text line.
// Cleared (null) whenever the user types manually, clears the field, or selects a
// registered card — so captured parts never lag the current text.
// showStatus (opt-in) — render a DEFAULT / PENDING (n/3) badge on each registered
// card (db-146/147). Used by the responder Edit-Address modal so the responder can
// see which address is the default + which still need verifying. Off by default so
// the other DeliverySelect uses (e.g. the device-delivery picker) are unchanged.
function DeliverySelect({
  value,
  onChange,
  onPartsChange,
  deliveryAddresses,
  locked,
  help,
  showStatus,
}) {
  const radioName = useId();
  function fmtAddr(a) {
    if (a._fromProfile) return a.street || "";
    const parts = [a.street, a.street2, a.suburb_city || a.suburb, a.state, a.postcode].filter(
      Boolean
    );
    return parts.join(", ");
  }
  function fmtAddrLabel(a) {
    if (a._fromProfile) return a.street || "";
    const addrStr = [a.street, a.street2, a.suburb_city || a.suburb, a.state, a.postcode]
      .filter(Boolean)
      .join(", ");
    return a.address_name ? `${a.address_name} — ${addrStr}` : addrStr;
  }

  var addrs = deliveryAddresses || [];
  var registeredValues = addrs.map(fmtAddr);
  var matchesRegistered = value && registeredValues.indexOf(value) !== -1;
  // "different" mode = user is entering / has entered an address that's not in
  // the registered list. We persist this via state so the toggle stays sticky
  // while the user clears + retypes.
  var initialDifferent = !!value && !matchesRegistered;
  var diffModeState = React.useState(initialDifferent);
  var differentMode = diffModeState[0];
  var setDifferentMode = diffModeState[1];

  // Keep differentMode in sync when value flips between registered and not.
  React.useEffect(
    function () {
      if (value && !matchesRegistered) setDifferentMode(true);
      if (matchesRegistered) setDifferentMode(false);
    },
    // Deps intentionally partial: only react to value changes;
    // differentMode itself is a user toggle.
    [value, matchesRegistered]
  );

  if (locked) return <LockedField label="Delivery Address" value={value} wide />;

  return (
    <div style={{ gridColumn: "1/-1" }}>
      {/* Caption for a control whose type varies (select or text input). */}
      <div
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 600,
          color: "#607D79",
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 5,
        }}
      >
        Delivery Address {help}
      </div>

      {/* List of registered addresses as radio-style cards. */}
      {addrs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {addrs.map(function (a, i) {
            var val = fmtAddr(a);
            var isSelected = !differentMode && value === val;
            return (
              <label
                key={a.id || i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "10px 12px",
                  border: isSelected ? `1.5px solid ${"#00897B"}` : "1.5px solid #e0e8e7",
                  background: isSelected ? "#E0F2F1" : "#fff",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                <input
                  type="radio"
                  name={radioName}
                  checked={isSelected}
                  onChange={function () {
                    setDifferentMode(false);
                    onChange(val);
                    if (onPartsChange) onPartsChange(null);
                  }}
                  style={{ marginTop: 3 }}
                />
                <span
                  style={{
                    color: "#2C3E3B",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span>{fmtAddrLabel(a)}</span>
                  {showStatus && a.is_default && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#fff",
                        background: "#00897B",
                        borderRadius: 4,
                        padding: "1px 6px",
                      }}
                    >
                      DEFAULT
                    </span>
                  )}
                  {showStatus && a.zephyr_verified === false && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#fff",
                        background: "#F57C00",
                        borderRadius: 4,
                        padding: "1px 6px",
                      }}
                    >
                      PENDING ({a.use_count || 0}/3)
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {/* Separate "different address" card — picking it always reveals an
          empty autocomplete (clears any prior value). onClick on the label
          fires on every click, including when the radio is already checked,
          so the user always gets a clean slate. */}
      <label
        onClick={function () {
          setDifferentMode(true);
          if (value) onChange("");
          if (onPartsChange) onPartsChange(null);
        }}
        onKeyDown={function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setDifferentMode(true);
            if (value) onChange("");
            if (onPartsChange) onPartsChange(null);
          }
        }}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "10px 12px",
          marginTop: addrs.length > 0 ? 6 : 0,
          border: differentMode ? `1.5px solid ${"#00897B"}` : "1.5px solid #e0e8e7",
          background: differentMode ? "#fffbeb" : "#fff",
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        <input
          type="radio"
          name={radioName}
          checked={differentMode}
          readOnly
          style={{ marginTop: 3 }}
        />
        <span style={{ color: "#2C3E3B" }}>
          Different address (enter manually){" "}
          <strong>&mdash; THIS MUST NOT BE THE CLIENT&rsquo;S ADDRESS</strong>
        </span>
      </label>

      {/* Input only appears when in "different" mode. */}
      {differentMode && (
        <div style={{ marginTop: 8 }}>
          <AddressAutocomplete
            value={value}
            onChange={function (text) {
              // Manual typing (or a pick's initial onChange, which fires BEFORE
              // onSelect) clears captured parts; a real pick re-sets them in
              // onSelect immediately after, so parts never lag the text.
              onChange(text);
              if (onPartsChange) onPartsChange(null);
            }}
            onSelect={function (parts) {
              var full = [parts.street, parts.suburb, parts.state, parts.postcode]
                .filter(Boolean)
                .join(", ");
              onChange(full);
              if (onPartsChange)
                onPartsChange({
                  line1: parts.street || "",
                  line2: parts.street2 || "",
                  suburb: parts.suburb || "",
                  state: parts.state || "",
                  postcode: parts.postcode || "",
                });
            }}
            placeholder="Start typing the new address..."
            inputStyle={{
              width: "100%",
              padding: "9px 12px",
              borderRadius: 8,
              border: !value?.trim() ? "1.5px solid #EF5350" : `1.5px solid ${"#00897B"}`,
              fontSize: 13,
              color: "#2C3E3B",
              background: value?.trim() ? "#E0F2F1" : "#fff",
              boxSizing: "border-box",
            }}
          />
          {!value?.trim() && (
            <div style={{ marginTop: 4, fontSize: 12, color: "#C62828" }}>
              Please enter a delivery address to continue.
            </div>
          )}
          {value?.trim() && !/\d{4}/.test(value) && (
            <div style={{ marginTop: 4, fontSize: 12, color: "#C62828" }}>
              Please include a 4-digit postcode in the address.
            </div>
          )}
          {value?.trim() && /\d{4}/.test(value) && !(registeredValues.indexOf(value) >= 0) && (
            <div
              style={{
                marginTop: 6,
                padding: "8px 12px",
                background: "#FFF3E0",
                border: "1px solid #FFB300",
                borderRadius: 6,
                fontSize: 12,
                color: "#7c4a00",
              }}
            >
              This address is not registered with Zephyr. A Responder will verify before
              dispatching.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { DeliverySelect };
